<template>
  <q-page class="ale-page">
    <!-- Loading -->
    <div v-if="loading" class="ale-loading">
      <q-skeleton type="rect" height="60px" style="margin-bottom: 16px;" />
      <q-skeleton type="rect" height="120px" style="margin-bottom: 16px;" />
      <q-skeleton type="rect" height="200px" />
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="ale-center-msg">
      <p style="color: var(--oa-danger); margin-bottom: 8px;">{{ loadError }}</p>
      <q-btn flat no-caps label="Reintentar" color="primary" @click="loadLesson" />
    </div>

    <!-- Content -->
    <template v-else-if="lesson">
      <!-- Header breadcrumb -->
      <div class="ale-header">
        <div class="ale-breadcrumb">
          <router-link :to="`/admin/courses/${courseId}/edit`" class="ale-crumb">
            Cursos
          </router-link>
          <span class="ale-crumb-sep">/</span>
          <span class="ale-crumb ale-crumb--active">{{ lesson.title }}</span>
        </div>

        <!-- Guardar leccion button -->
        <q-btn
          no-caps
          color="primary"
          :loading="saving"
          :disable="saving"
          @click="handleSave"
        >
          <template #default>
            <q-icon v-if="!saving" name="save" size="16px" style="margin-right: 6px;" />
            Guardar lección
          </template>
        </q-btn>
      </div>

      <!-- Metadata strip (q-expansion-item, collapsed by default) -->
      <q-expansion-item
        v-model="metaOpen"
        label="Información de la lección"
        header-class="ale-meta-header"
        expand-icon-class="text-primary"
        class="ale-meta-strip"
      >
        <div class="ale-meta-form">
          <q-input
            outlined
            v-model="metaTitle"
            label="Título de la lección"
          />
          <q-input
            outlined
            type="textarea"
            v-model="metaDescription"
            label="Descripción"
            rows="2"
          />
          <q-input
            outlined
            type="number"
            v-model.number="metaMinutes"
            label="Minutos estimados"
          />

          <!-- Video de la leccion (subida directa a R2) -->
          <div class="ale-video-field">
            <!-- Ya hay video: preview + eliminar -->
            <div v-if="lesson.videoUrl" class="ale-video-current">
              <video
                :key="lesson.videoUrl"
                :src="lesson.videoUrl"
                controls
                preload="metadata"
                class="ale-video-preview"
              />
              <q-btn
                flat
                no-caps
                dense
                icon="delete"
                label="Eliminar video"
                color="negative"
                :disable="videoUploading"
                @click="onRemoveVideo"
              />
            </div>

            <!-- Sin video: subir -->
            <div v-else>
              <q-file
                outlined
                :model-value="null"
                label="Video de la lección"
                accept="video/*,.mp4,.mov,.webm,.m4v"
                :disable="videoUploading"
                :style="{ '--q-field-border-color': 'var(--color-sun-gold, #e8a020)' }"
                class="ale-video-input"
                hint="MP4, MOV, WEBM o M4V (hasta 2 GB)."
                @update:model-value="onPickVideo"
              >
                <template #prepend>
                  <q-icon name="video_file" :style="{ color: 'var(--color-sun-gold, #e8a020)' }" />
                </template>
              </q-file>

              <div v-if="videoUploading" class="ale-video-uploading">
                <q-linear-progress
                  :value="videoProgress / 100"
                  color="primary"
                  size="8px"
                  rounded
                />
                <span class="ale-video-progress-label">Subiendo… {{ videoProgress }}%</span>
              </div>
            </div>
          </div>
        </div>
      </q-expansion-item>

      <!-- Main two-column layout: edit + preview -->
      <div class="ale-columns" :class="{ 'ale-columns--single': !showPreviewColumn }">
        <!-- EDIT COLUMN -->
        <div class="ale-edit-col">
          <!-- Preview toggle for tablet/mobile -->
          <div v-if="isTablet" class="preview-toggle-row">
            <q-btn
              flat
              no-caps
              :icon="showPreviewColumn ? 'visibility_off' : 'visibility'"
              :label="showPreviewColumn ? 'Ocultar preview' : 'Ver preview'"
              size="sm"
              color="primary"
              @click="showPreviewColumn = !showPreviewColumn"
            />
          </div>

          <BlockEditor />
        </div>

        <!-- PREVIEW COLUMN (desktop/tablet toggle) -->
        <div v-if="showPreviewColumn && !isMobile" class="ale-preview-col">
          <div class="ale-preview-header">
            <q-icon name="visibility" size="14px" />
            Preview
          </div>
          <div class="ale-preview-content">
            <LessonRenderer :rich-content="store.richContent" :text-content="null" />
          </div>
        </div>
      </div>

      <!-- Mobile preview bottom sheet -->
      <div v-if="isMobile" class="ale-mobile-preview-btn">
        <q-btn
          unelevated
          no-caps
          icon="visibility"
          label="Previsualizar"
          color="primary"
          @click="showMobilePreview = true"
        />
      </div>

      <q-dialog v-model="showMobilePreview" position="bottom" full-height>
        <q-card style="width: 100%; height: 80vh; overflow-y: auto;">
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h6" style="font-size: 15px; font-weight: 600;">Preview</div>
            <q-space />
            <q-btn icon="close" flat round dense @click="showMobilePreview = false" />
          </q-card-section>
          <LessonRenderer :rich-content="store.richContent" :text-content="null" />
        </q-card>
      </q-dialog>
    </template>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { onBeforeRouteLeave } from 'vue-router';
import { useQuasar } from 'quasar';
import { useAdminCoursesStore } from 'src/stores/useAdminCoursesStore';
import { useVideoUpload } from 'src/composables/useVideoUpload';
import BlockEditor from 'src/components/admin/BlockEditor.vue';
import LessonRenderer from 'src/components/lesson/LessonRenderer.vue';

const route = useRoute();
const $q = useQuasar();
const store = useAdminCoursesStore();

const courseId = computed(() => route.params.id as string);
const lessonId = computed(() => route.params.lid as string);

// Local state
const loading = ref(false);
const loadError = ref<string | null>(null);
const saving = ref(false);
const metaOpen = ref(false);
const showMobilePreview = ref(false);

// Metadata form fields
const metaTitle = ref('');
const metaDescription = ref('');
const metaMinutes = ref<number | null>(null);

// Track if metadata was changed
const originalMeta = ref({ title: '', description: '', estimatedMinutes: null as number | null });
const metaDirty = computed(() => {
  return (
    metaTitle.value !== originalMeta.value.title ||
    metaDescription.value !== originalMeta.value.description ||
    metaMinutes.value !== originalMeta.value.estimatedMinutes
  );
});

// Responsive layout
const isMobile = computed(() => $q.screen.width < 768);
const isTablet = computed(() => $q.screen.width >= 768 && $q.screen.width < 1024);
const showPreviewColumn = ref(true);

// Sync preview column visibility with responsive breakpoint
watch(isMobile, (mobile) => {
  if (mobile) showPreviewColumn.value = false;
});
watch(isTablet, (tablet) => {
  if (!tablet && !isMobile.value) showPreviewColumn.value = true;
});

// Find lesson from the store (or null)
const lesson = computed(() => {
  if (!store.editingCourse?.units) return null;
  for (const unit of store.editingCourse.units) {
    const found = unit.lessons.find((l) => l.id === lessonId.value);
    if (found) return found;
  }
  return null;
});

async function loadLesson() {
  loading.value = true;
  loadError.value = null;
  try {
    // Fetch course if not loaded or different course
    if (!store.editingCourse || store.editingCourse.id !== courseId.value) {
      await store.fetchCourse(courseId.value);
    }
    // Now find lesson and initialize blocks
    if (lesson.value) {
      store.setBlocks(lesson.value.richContent ?? []);
      metaTitle.value = lesson.value.title;
      metaDescription.value = lesson.value.description ?? '';
      metaMinutes.value = lesson.value.estimatedMinutes ?? null;
      originalMeta.value = {
        title: lesson.value.title,
        description: lesson.value.description ?? '',
        estimatedMinutes: lesson.value.estimatedMinutes ?? null,
      };
    } else if (store.editError) {
      // fetchCourse traga el error interno en editError: no confundir un fallo
      // de red/servidor con una leccion inexistente.
      loadError.value = store.editError;
    } else {
      loadError.value = 'Lección no encontrada.';
    }
  } catch {
    loadError.value = 'Error al cargar. Intentalo de nuevo.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadLesson();
});

// Re-run loadLesson if the route param changes (e.g. navigating between lessons
// in the same course without unmounting the component)
watch(lessonId, () => {
  void loadLesson();
});

// === Video (subida a R2) ===
const {
  uploading: videoUploading,
  progress: videoProgress,
  validate: validateVideo,
  uploadVideo,
  deleteVideo,
} = useVideoUpload();

async function onPickVideo(file: File | null) {
  if (!file || !lesson.value) return;
  const err = validateVideo(file);
  if (err) {
    $q.notify({ type: 'negative', message: err });
    return;
  }
  try {
    const videoUrl = await uploadVideo(lessonId.value, file);
    store.setLessonVideo(lessonId.value, videoUrl);
    $q.notify({ type: 'positive', message: 'Video subido correctamente' });
  } catch {
    $q.notify({ type: 'negative', message: 'Error al subir el video. Intentalo de nuevo.' });
  }
}

async function onRemoveVideo() {
  if (!lesson.value?.videoUrl) return;
  try {
    await deleteVideo(lessonId.value);
    store.setLessonVideo(lessonId.value, null);
    $q.notify({ type: 'positive', message: 'Video eliminado' });
  } catch {
    $q.notify({ type: 'negative', message: 'No se pudo eliminar el video. Intentalo de nuevo.' });
  }
}

async function handleSave() {
  // El backend rechaza un titulo vacio con 400, que caeria en el catch generico
  // como si fuese un problema de conexion. Validamos antes en el cliente.
  if (metaDirty.value && !metaTitle.value.trim()) {
    $q.notify({ type: 'negative', message: 'El título de la lección no puede estar vacío' });
    return;
  }
  saving.value = true;
  try {
    // Save metadata if changed
    if (metaDirty.value) {
      const body: { title?: string; description?: string; estimatedMinutes?: number } = {};
      if (metaTitle.value !== originalMeta.value.title) body.title = metaTitle.value;
      if (metaDescription.value !== originalMeta.value.description) body.description = metaDescription.value;
      if (metaMinutes.value !== null && metaMinutes.value !== originalMeta.value.estimatedMinutes) {
        body.estimatedMinutes = metaMinutes.value;
      }
      await store.updateLesson(lessonId.value, body);
      originalMeta.value = {
        title: metaTitle.value,
        description: metaDescription.value,
        estimatedMinutes: metaMinutes.value,
      };
    }

    // Save block content if dirty
    if (store.isDirty) {
      await store.saveContent(lessonId.value);
    }

    $q.notify({ type: 'positive', message: 'Lección guardada' });
  } catch (err: unknown) {
    const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    $q.notify({
      type: 'negative',
      message: msg || 'No se pudo guardar. Revisá tu conexión e intentalo de nuevo.',
    });
  } finally {
    saving.value = false;
  }
}

// Unsaved changes guard
onBeforeRouteLeave((_to, _from, next) => {
  if (!store.isDirty && !metaDirty.value) {
    next();
    return;
  }
  $q.dialog({
    title: 'Cambios sin guardar',
    message: 'Tenés cambios sin guardar. ¿Salir de todos modos?',
    cancel: { label: 'Quedarme', flat: true },
    ok: { label: 'Salir sin guardar', flat: true, color: 'negative' },
    persistent: true,
  })
    .onOk(() => next())
    .onCancel(() => next(false));
});
</script>

<style scoped lang="scss">
.ale-page {
  background: var(--color-seafoam, #e8f4f8);
  min-height: 100vh;
}

.ale-loading,
.ale-center-msg {
  padding: 32px;
  max-width: 720px;
  margin: 0 auto;
}

.ale-center-msg {
  text-align: center;
  padding-top: 64px;
}

/* Header */
.ale-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: var(--oa-bg-2);
  border-bottom: 1px solid var(--oa-border);
  flex-wrap: wrap;
  gap: 12px;
}

.ale-breadcrumb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.ale-crumb {
  color: var(--oa-text-muted);
  text-decoration: none;

  &:hover {
    color: var(--color-ocean-light);
  }

  &--active {
    color: var(--oa-text-primary);
    font-weight: 600;
  }
}

.ale-crumb-sep {
  color: var(--oa-text-disabled);
}

/* Metadata strip */
.ale-meta-strip {
  background: var(--color-sand-light, #f5efe6);
  border-bottom: 1px solid var(--oa-border);
}

:deep(.ale-meta-header) {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  min-height: 44px;
}

.ale-meta-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px 20px;
}

/* Video field — sun-gold border override */
.ale-video-input {
  :deep(.q-field__control) {
    border-color: var(--color-sun-gold, #e8a020) !important;
  }
  :deep(.q-field--outlined .q-field__control:before) {
    border-color: var(--color-sun-gold, #e8a020) !important;
  }
}

/* Video preview + eliminar (cuando ya hay video subido) */
.ale-video-current {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.ale-video-preview {
  width: 100%;
  max-height: 240px;
  border-radius: var(--radius-base, 8px);
  background: #000;
}
.ale-video-uploading {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
.ale-video-progress-label {
  font-size: 12px;
  color: var(--oa-text-secondary);
  white-space: nowrap;
}

/* Two-column layout */
.ale-columns {
  display: grid;
  grid-template-columns: 55% 45%;
  min-height: calc(100vh - 200px);

  &--single {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
}

.ale-edit-col {
  border-right: 1px solid var(--oa-border);
  display: flex;
  flex-direction: column;

  @media (max-width: 1023px) {
    border-right: none;
  }
}

.preview-toggle-row {
  padding: 8px 16px;
  border-bottom: 1px solid var(--oa-border);
}

.ale-preview-col {
  display: flex;
  flex-direction: column;
  background: var(--oa-bg-2);
}

.ale-preview-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-ocean-light);
  border-bottom: 1px solid var(--oa-border);
  border-left: 4px solid var(--color-ocean-light);
  background: color-mix(in srgb, var(--color-ocean-light) 5%, transparent);
}

.ale-preview-content {
  flex: 1;
  overflow-y: auto;
}

.ale-mobile-preview-btn {
  position: fixed;
  bottom: 16px;
  right: 16px;
  z-index: 10;
}
</style>
